/**
 * Enhanced Wealth Gap Calculator Modal
 * Comprehensive wealth gap calculations with all parameters
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Target, DollarSign, PiggyBank, TrendingUp, Calendar, Users, Save, X, Percent, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import HelpTooltip from '../common/HelpTooltip';
import AllocationInputField from './AllocationInputField';
import { calculateWealthGap as calculateWealthGapAPI } from '../../services/api';

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, color, isExpanded, onToggle, children }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', hover: 'hover:bg-blue-100' },
    green: { bg: 'bg-green-50', border: 'border-green-200', hover: 'hover:bg-green-100' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', hover: 'hover:bg-purple-100' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', hover: 'hover:bg-yellow-100' },
    pink: { bg: 'bg-pink-50', border: 'border-pink-200', hover: 'hover:bg-pink-100' },
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', hover: 'hover:bg-gray-100' }
  };

  const colors = colorMap[color] || colorMap.gray;

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-xl overflow-hidden`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-4 transition-colors ${colors.hover}`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        </div>
        <ChevronDown
          size={20}
          className={`text-gray-600 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
};

const WealthGapCalculatorModalEnhanced = ({
  show,
  onClose,
  wealthGap,
  businessProfile,
  profitGap,
  valueGap,
  onSave,
  saving
}) => {
  // State for all fields
  const [wealthGapMethod, setWealthGapMethod] = useState('monthly_needs');
  const [wealthGoalAmount, setWealthGoalAmount] = useState('');
  const [nonBusinessNetWorth, setNonBusinessNetWorth] = useState(''); // For single_number method

  // Monthly needs fields
  const [monthlyCashNeed, setMonthlyCashNeed] = useState('');
  const [yearsOfIncome, setYearsOfIncome] = useState('20');
  const [inflationRate, setInflationRate] = useState('2');
  const [returnRate, setReturnRate] = useState('5');

  // Outside income
  const [outsideIncomeMonthly, setOutsideIncomeMonthly] = useState('');
  const [outsideIncomeAnnual, setOutsideIncomeAnnual] = useState('');
  const [outsideIncomeAnnualIncrease, setOutsideIncomeAnnualIncrease] = useState('0');

  // Age and retirement
  const [currentAge, setCurrentAge] = useState('50');
  const [retirementAge, setRetirementAge] = useState('65');
  const [lifeExpectancy, setLifeExpectancy] = useState('100');
  const [yearsToRetirement, setYearsToRetirement] = useState('15'); // New field

  // Business sale parameters
  const [taxRateOnSale, setTaxRateOnSale] = useState('42');
  const [ownershipPercentage, setOwnershipPercentage] = useState('100');
  const [desiredSalePrice, setDesiredSalePrice] = useState('');
  const [businessGrowthRate, setBusinessGrowthRate] = useState('0');
  const [exitYear, setExitYear] = useState(new Date().getFullYear() + 2);

  // Sale proceeds allocation
  const [lumpSumInputMode, setLumpSumInputMode] = useState('percent'); // 'percent' or 'dollar'
  const [lumpSumPercent, setLumpSumPercent] = useState('50');
  const [lumpSumDollar, setLumpSumDollar] = useState('');

  // Earnout configuration
  const [earnoutYears, setEarnoutYears] = useState('5');
  const [earnoutInterestRate, setEarnoutInterestRate] = useState('5');
  const [earnoutPaymentFrequency, setEarnoutPaymentFrequency] = useState('annual'); // 'annual' or 'monthly'
  const [earnoutTaxRate, setEarnoutTaxRate] = useState('42'); // Separate tax rate for earnout

  // Lump sum allocation across asset categories (percentages should sum to 100)
  const [lumpSumAllocationLiquidAssets, setLumpSumAllocationLiquidAssets] = useState('0');
  const [lumpSumAllocationRealEstate, setLumpSumAllocationRealEstate] = useState('0');
  const [lumpSumAllocationOtherInvestments, setLumpSumAllocationOtherInvestments] = useState('100');

  // Earnout allocation across asset categories (percentages should sum to 100)
  const [earnoutAllocationLiquidAssets, setEarnoutAllocationLiquidAssets] = useState('0');
  const [earnoutAllocationRealEstate, setEarnoutAllocationRealEstate] = useState('0');
  const [earnoutAllocationOtherInvestments, setEarnoutAllocationOtherInvestments] = useState('100');

  // Lifestyle changes
  const [lifestyleChangePercentage, setLifestyleChangePercentage] = useState('0');

  // Current financial position
  const [liquidAssets, setLiquidAssets] = useState('');
  const [retirementAccounts, setRetirementAccounts] = useState('');
  const [realEstateEquity, setRealEstateEquity] = useState('');
  const [otherInvestments, setOtherInvestments] = useState('');
  const [totalLiabilities, setTotalLiabilities] = useState('');

  // New cash flow allocation fields
  const [monthlyCashNeedFromNonBusiness, setMonthlyCashNeedFromNonBusiness] = useState('');
  const [nonBusinessSourceCategory, setNonBusinessSourceCategory] = useState('liquid_assets');
  const [postExitSourceCategory, setPostExitSourceCategory] = useState('liquid_assets');
  const [monthlyCashNeedFromBusiness, setMonthlyCashNeedFromBusiness] = useState('');

  // Toggle states for percentage vs dollar input
  const [nonBusinessInputMode, setNonBusinessInputMode] = useState('percent'); // 'percent' or 'dollar'
  const [businessInputMode, setBusinessInputMode] = useState('percent'); // 'percent' or 'dollar'
  const [outsideIncomeInputMode, setOutsideIncomeInputMode] = useState('percent'); // 'percent' or 'dollar'

  // Percentage values (when in percent mode)
  const [nonBusinessPercent, setNonBusinessPercent] = useState('');
  const [businessPercent, setBusinessPercent] = useState('');
  const [outsideIncomePercent, setOutsideIncomePercent] = useState('');

  // Legacy field for backwards compatibility
  const [nonBusinessMonthlyCashNeeds, setNonBusinessMonthlyCashNeeds] = useState('');

  // Individual return rates for each asset (pre-exit)
  const [liquidAssetsReturnRate, setLiquidAssetsReturnRate] = useState('3');
  const [retirementAccountsReturnRate, setRetirementAccountsReturnRate] = useState('7');
  const [realEstateReturnRate, setRealEstateReturnRate] = useState('4');
  const [otherInvestmentsReturnRate, setOtherInvestmentsReturnRate] = useState('6');
  const [liabilityReductionRate, setLiabilityReductionRate] = useState('0');

  // Post-exit return rates (from exit year to life expectancy)
  const [postExitLiquidAssetsReturnRate, setPostExitLiquidAssetsReturnRate] = useState('3');
  const [postExitRetirementAccountsReturnRate, setPostExitRetirementAccountsReturnRate] = useState('7');
  const [postExitRealEstateReturnRate, setPostExitRealEstateReturnRate] = useState('4');
  const [postExitOtherInvestmentsReturnRate, setPostExitOtherInvestmentsReturnRate] = useState('6');
  const [postExitLiabilityReductionRate, setPostExitLiabilityReductionRate] = useState('0');

  // Section collapse state
  const [expandedSections, setExpandedSections] = useState({
    lifestyle: true,
    ageTimeline: false,
    businessSale: false,
    currentFinancial: false
  });

  // State for calculated values from backend API
  const [calculatedData, setCalculatedData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const calculationTimeoutRef = useRef(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Debounced function to call backend API for calculations
  const fetchCalculatedValues = useCallback(async () => {
    // Only calculate if we have the minimum required fields
    if (!monthlyCashNeed || parseFloat(monthlyCashNeed) === 0) {
      setCalculatedData(null);
      return;
    }

    setIsCalculating(true);

    try {
      // Calculate current net worth from assets
      const calculatedNetWorth = (
        (parseFloat(liquidAssets) || 0) +
        (parseFloat(retirementAccounts) || 0) +
        (parseFloat(realEstateEquity) || 0) +
        (parseFloat(otherInvestments) || 0) -
        (parseFloat(totalLiabilities) || 0)
      );

      const formData = {
        wealth_goal_method: wealthGapMethod,
        wealth_goal_amount: parseFloat(wealthGoalAmount) || 0,
        monthly_cash_need: parseFloat(monthlyCashNeed) || 0,
        non_business_monthly_cash_needs: parseFloat(nonBusinessMonthlyCashNeeds) || 0,
        years_of_income: parseInt(yearsOfIncome) || 20,
        annual_inflation_rate: parseFloat(inflationRate) || 2,
        annual_return_rate: parseFloat(returnRate) || 5,
        outside_income_monthly: parseFloat(outsideIncomeMonthly) || 0,
        outside_income_annual: parseFloat(outsideIncomeAnnual) || 0,
        outside_income_annual_increase: parseFloat(outsideIncomeAnnualIncrease) || 0,
        current_age: parseInt(currentAge) || 50,
        retirement_age: parseInt(retirementAge) || 65,
        life_expectancy: parseInt(lifeExpectancy) || 100,
        tax_rate_on_sale: taxRateOnSale === '' ? 42 : parseFloat(taxRateOnSale),
        ownership_percentage: parseFloat(ownershipPercentage) || 100,
        desired_sale_price: parseFloat(desiredSalePrice) || 0,
        business_growth_rate: parseFloat(businessGrowthRate) || 0,
        exit_year: parseInt(exitYear) || new Date().getFullYear() + 2,
        lifestyle_change_percentage: parseFloat(lifestyleChangePercentage) || 0,
        current_net_worth: calculatedNetWorth,
        liquid_assets: parseFloat(liquidAssets) || 0,
        retirement_accounts: parseFloat(retirementAccounts) || 0,
        real_estate_equity: parseFloat(realEstateEquity) || 0,
        other_investments: parseFloat(otherInvestments) || 0,
        total_liabilities: parseFloat(totalLiabilities) || 0,
        liquid_assets_return_rate: parseFloat(liquidAssetsReturnRate) || 3,
        retirement_accounts_return_rate: parseFloat(retirementAccountsReturnRate) || 7,
        real_estate_return_rate: parseFloat(realEstateReturnRate) || 4,
        other_investments_return_rate: parseFloat(otherInvestmentsReturnRate) || 6,
        liability_reduction_rate: parseFloat(liabilityReductionRate) || 0,
        monthly_cash_need_from_non_business: parseFloat(monthlyCashNeedFromNonBusiness) || 0,
        monthly_cash_need_from_business: parseFloat(monthlyCashNeedFromBusiness) || 0,
        non_business_source_category: nonBusinessSourceCategory
      };

      const result = await calculateWealthGapAPI(formData);

      if (result.success) {
        setCalculatedData(result.data);
      } else {
        console.error('Calculation error:', result.error);
      }
    } catch (error) {
      console.error('Error fetching calculated values:', error);
    } finally {
      setIsCalculating(false);
    }
  }, [
    wealthGapMethod, wealthGoalAmount, monthlyCashNeed, nonBusinessMonthlyCashNeeds, yearsOfIncome,
    inflationRate, returnRate, outsideIncomeMonthly, outsideIncomeAnnual, outsideIncomeAnnualIncrease,
    currentAge, retirementAge, lifeExpectancy, taxRateOnSale, ownershipPercentage,
    desiredSalePrice, businessGrowthRate, exitYear, lifestyleChangePercentage,
    liquidAssets, retirementAccounts, realEstateEquity, otherInvestments, totalLiabilities,
    liquidAssetsReturnRate, retirementAccountsReturnRate, realEstateReturnRate,
    otherInvestmentsReturnRate, liabilityReductionRate, monthlyCashNeedFromNonBusiness,
    monthlyCashNeedFromBusiness, nonBusinessSourceCategory
  ]);

  // Debounce the calculation API call (500ms delay after user stops typing)
  useEffect(() => {
    if (!show) return;

    // Clear any existing timeout
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    // Set new timeout
    calculationTimeoutRef.current = setTimeout(() => {
      fetchCalculatedValues();
    }, 500);

    // Cleanup
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [show, fetchCalculatedValues]);

  // Auto-populate desired sale price from profit gap current valuation or value gap current exit value
  useEffect(() => {
    if (show) {
      // Prioritize profitGap.current_valuation, fall back to valueGap.current_exit_value
      if (profitGap?.current_valuation) {
        setDesiredSalePrice(String(profitGap.current_valuation));
      } else if (valueGap?.current_exit_value) {
        setDesiredSalePrice(String(valueGap.current_exit_value));
      }
    }
  }, [profitGap, valueGap, show]);

  // Calculate current age from DOB when businessProfile changes
  useEffect(() => {
    if (businessProfile?.client_date_of_birth && show) {
      const calculatedAge = calculateAge(businessProfile.client_date_of_birth);
      if (calculatedAge !== null && calculatedAge > 0) {
        setCurrentAge(String(calculatedAge));
      }
    }
  }, [businessProfile?.client_date_of_birth, show]);

  // Auto-calculate exit year when current age or years to retirement changes
  useEffect(() => {
    if (currentAge && yearsToRetirement) {
      const currentYear = new Date().getFullYear();
      const calculatedExitYear = currentYear + parseInt(yearsToRetirement);
      setExitYear(calculatedExitYear);
    }
  }, [currentAge, yearsToRetirement]);

  // Auto-calculate years of income when retirement age or life expectancy changes
  useEffect(() => {
    const retAge = parseInt(retirementAge) || 65;
    const lifeExp = parseInt(lifeExpectancy) || 100;
    const calculated = Math.max(0, lifeExp - retAge);
    setYearsOfIncome(String(calculated));
  }, [retirementAge, lifeExpectancy]);

  // Calculate years to retirement when retirement age or current age changes
  useEffect(() => {
    const curAge = parseInt(currentAge) || 50;
    const retAge = parseInt(retirementAge) || 65;
    const yearsTo = Math.max(0, retAge - curAge);
    setYearsToRetirement(String(yearsTo));
  }, [currentAge, retirementAge]);

  // Load existing data
  useEffect(() => {
    if (wealthGap && show) {
      setWealthGapMethod(wealthGap.wealth_goal_method || 'monthly_needs');
      setWealthGoalAmount(String(wealthGap.wealth_goal_amount || ''));
      setMonthlyCashNeed(String(wealthGap.monthly_cash_need || ''));
      setInflationRate(String(wealthGap.annual_inflation_rate || '2'));
      setReturnRate(String(wealthGap.annual_return_rate || '5'));
      setOutsideIncomeMonthly(String(wealthGap.outside_income_monthly || ''));
      setOutsideIncomeAnnual(String(wealthGap.outside_income_annual || ''));
      setOutsideIncomeAnnualIncrease(String(wealthGap.outside_income_annual_increase || '0'));

      // Don't load currentAge from saved data - let it be calculated from DOB
      // if (wealthGap.current_age) setCurrentAge(String(wealthGap.current_age));
      if (wealthGap.retirement_age) setRetirementAge(String(wealthGap.retirement_age || '65'));
      if (wealthGap.life_expectancy) setLifeExpectancy(String(wealthGap.life_expectancy || '100'));

      setTaxRateOnSale(wealthGap.tax_rate_on_sale !== undefined ? String(wealthGap.tax_rate_on_sale) : '42');
      setOwnershipPercentage(String(wealthGap.ownership_percentage || '100'));
      setDesiredSalePrice(String(wealthGap.desired_sale_price || ''));
      setBusinessGrowthRate(String(wealthGap.business_growth_rate || '0'));
      if (wealthGap.exit_year) setExitYear(wealthGap.exit_year);
      setLifestyleChangePercentage(String(wealthGap.lifestyle_change_percentage || '0'));
      setLiquidAssets(String(wealthGap.liquid_assets || ''));
      setRetirementAccounts(String(wealthGap.retirement_accounts || ''));
      setRealEstateEquity(String(wealthGap.real_estate_equity || ''));
      setOtherInvestments(String(wealthGap.other_investments || ''));
      setTotalLiabilities(String(wealthGap.total_liabilities || ''));
      setNonBusinessNetWorth(String(wealthGap.non_business_net_worth || ''));

      // New cash flow allocation fields
      setMonthlyCashNeedFromNonBusiness(String(wealthGap.monthly_cash_need_from_non_business || ''));
      setNonBusinessSourceCategory(wealthGap.non_business_source_category || 'liquid_assets');
      setMonthlyCashNeedFromBusiness(String(wealthGap.monthly_cash_need_from_business || ''));

      // Calculate and set percent values for allocation fields
      const totalNeed = parseFloat(wealthGap.monthly_cash_need) || 0;
      if (totalNeed > 0) {
        const nonBusinessDollar = parseFloat(wealthGap.monthly_cash_need_from_non_business) || 0;
        const businessDollar = parseFloat(wealthGap.monthly_cash_need_from_business) || 0;
        const outsideDollar = parseFloat(wealthGap.outside_income_monthly) || 0;

        setNonBusinessPercent(((nonBusinessDollar / totalNeed) * 100).toFixed(1));
        setBusinessPercent(((businessDollar / totalNeed) * 100).toFixed(1));
        setOutsideIncomePercent(((outsideDollar / totalNeed) * 100).toFixed(1));
      }

      // Legacy field
      setNonBusinessMonthlyCashNeeds(String(wealthGap.non_business_monthly_cash_needs || ''));

      // Individual return rates - use !== undefined to allow 0% returns
      setLiquidAssetsReturnRate(String(wealthGap.liquid_assets_return_rate !== undefined ? wealthGap.liquid_assets_return_rate : '3'));
      setRetirementAccountsReturnRate(String(wealthGap.retirement_accounts_return_rate !== undefined ? wealthGap.retirement_accounts_return_rate : '7'));
      setRealEstateReturnRate(String(wealthGap.real_estate_return_rate !== undefined ? wealthGap.real_estate_return_rate : '4'));
      setOtherInvestmentsReturnRate(String(wealthGap.other_investments_return_rate !== undefined ? wealthGap.other_investments_return_rate : '6'));
      setLiabilityReductionRate(String(wealthGap.liability_reduction_rate !== undefined ? wealthGap.liability_reduction_rate : '0'));

      // Post-exit return rates
      setPostExitLiquidAssetsReturnRate(String(wealthGap.post_exit_liquid_assets_return_rate !== undefined ? wealthGap.post_exit_liquid_assets_return_rate : '3'));
      setPostExitRetirementAccountsReturnRate(String(wealthGap.post_exit_retirement_accounts_return_rate !== undefined ? wealthGap.post_exit_retirement_accounts_return_rate : '7'));
      setPostExitRealEstateReturnRate(String(wealthGap.post_exit_real_estate_return_rate !== undefined ? wealthGap.post_exit_real_estate_return_rate : '4'));
      setPostExitOtherInvestmentsReturnRate(String(wealthGap.post_exit_other_investments_return_rate !== undefined ? wealthGap.post_exit_other_investments_return_rate : '6'));
      setPostExitLiabilityReductionRate(String(wealthGap.post_exit_liability_reduction_rate !== undefined ? wealthGap.post_exit_liability_reduction_rate : '0'));

      // Post-exit source category
      setPostExitSourceCategory(wealthGap.post_exit_source_category || 'liquid_assets');

      // Earnout configuration
      setEarnoutYears(String(wealthGap.earnout_years !== undefined ? wealthGap.earnout_years : '5'));
      setEarnoutInterestRate(String(wealthGap.earnout_interest_rate !== undefined ? wealthGap.earnout_interest_rate : '5'));
      setEarnoutTaxRate(String(wealthGap.earnout_tax_rate !== undefined ? wealthGap.earnout_tax_rate : '42'));
      setEarnoutPaymentFrequency(wealthGap.earnout_payment_frequency || 'annual');

      // Lump sum configuration
      setLumpSumPercent(String(wealthGap.lump_sum_percent !== undefined ? wealthGap.lump_sum_percent : '30'));
      setLumpSumDollar(String(wealthGap.lump_sum_dollar !== undefined ? wealthGap.lump_sum_dollar : ''));

      // Lump sum allocation
      setLumpSumAllocationLiquidAssets(String(wealthGap.lump_sum_allocation_liquid_assets !== undefined ? wealthGap.lump_sum_allocation_liquid_assets : '0'));
      setLumpSumAllocationRealEstate(String(wealthGap.lump_sum_allocation_real_estate !== undefined ? wealthGap.lump_sum_allocation_real_estate : '0'));
      setLumpSumAllocationOtherInvestments(String(wealthGap.lump_sum_allocation_other_investments !== undefined ? wealthGap.lump_sum_allocation_other_investments : '100'));

      // Earnout allocation
      setEarnoutAllocationLiquidAssets(String(wealthGap.earnout_allocation_liquid_assets !== undefined ? wealthGap.earnout_allocation_liquid_assets : '0'));
      setEarnoutAllocationRealEstate(String(wealthGap.earnout_allocation_real_estate !== undefined ? wealthGap.earnout_allocation_real_estate : '0'));
      setEarnoutAllocationOtherInvestments(String(wealthGap.earnout_allocation_other_investments !== undefined ? wealthGap.earnout_allocation_other_investments : '100'));
    }
  }, [wealthGap, show]);

  // Helper functions for percentage/dollar conversion
  const calculateDollarFromPercent = (percent, total) => {
    const p = parseFloat(percent) || 0;
    const t = parseFloat(total) || 0;
    return (p / 100) * t;
  };

  const calculatePercentFromDollar = (dollar, total) => {
    const d = parseFloat(dollar) || 0;
    const t = parseFloat(total) || 0;
    if (t === 0) return 0;
    return (d / t) * 100;
  };

  // Validation: Check if allocations add up to 100%
  const validateAllocation = () => {
    // Skip validation for single_number method
    if (wealthGapMethod === 'single_number') {
      return { valid: true, message: '' };
    }

    const totalMonthlyNeed = parseFloat(monthlyCashNeed) || 0;
    if (totalMonthlyNeed === 0) return { valid: true, message: '' };

    const nonBusiness = parseFloat(monthlyCashNeedFromNonBusiness) || 0;
    const business = parseFloat(monthlyCashNeedFromBusiness) || 0;
    const outsideIncome = parseFloat(outsideIncomeMonthly) || 0;

    const totalAllocated = nonBusiness + business + outsideIncome;
    const diff = Math.abs(totalAllocated - totalMonthlyNeed);

    // Allow for small rounding errors (within $1)
    if (diff > 1) {
      const percentAllocated = ((totalAllocated / totalMonthlyNeed) * 100).toFixed(1);
      return {
        valid: false,
        message: `Allocation must equal 100% of monthly cash need. Currently: ${percentAllocated}% (${formatCurrency(totalAllocated)} of ${formatCurrency(totalMonthlyNeed)})`
      };
    }

    return { valid: true, message: '' };
  };

  const handleSave = () => {
    // Validate allocation before saving
    const validation = validateAllocation();
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    const data = {
      wealth_goal_method: wealthGapMethod,
      wealth_goal_amount: parseFloat(wealthGoalAmount) || 0,
      monthly_cash_need: parseFloat(monthlyCashNeed) || 0,
      years_of_income: parseInt(yearsOfIncome) || 20,
      annual_inflation_rate: parseFloat(inflationRate) || 2,
      annual_return_rate: parseFloat(returnRate) || 5,
      outside_income_monthly: parseFloat(outsideIncomeMonthly) || 0,
      outside_income_annual: parseFloat(outsideIncomeAnnual) || 0,
      outside_income_annual_increase: parseFloat(outsideIncomeAnnualIncrease) || 0,
      current_age: parseInt(currentAge) || 50,
      retirement_age: parseInt(retirementAge) || 65,
      life_expectancy: parseInt(lifeExpectancy) || 100,
      tax_rate_on_sale: taxRateOnSale === '' ? 42 : parseFloat(taxRateOnSale),
      ownership_percentage: parseFloat(ownershipPercentage) || 100,
      desired_sale_price: parseFloat(desiredSalePrice) || 0,
      business_growth_rate: parseFloat(businessGrowthRate) || 0,
      exit_year: parseInt(exitYear) || new Date().getFullYear() + 2,
      lifestyle_change_percentage: parseFloat(lifestyleChangePercentage) || 0,
      liquid_assets: parseFloat(liquidAssets) || 0,
      retirement_accounts: parseFloat(retirementAccounts) || 0,
      real_estate_equity: parseFloat(realEstateEquity) || 0,
      other_investments: parseFloat(otherInvestments) || 0,
      total_liabilities: parseFloat(totalLiabilities) || 0,
      non_business_net_worth: parseFloat(nonBusinessNetWorth) || 0,
      // New cash flow allocation fields
      monthly_cash_need_from_non_business: parseFloat(monthlyCashNeedFromNonBusiness) || 0,
      non_business_source_category: nonBusinessSourceCategory,
      monthly_cash_need_from_business: parseFloat(monthlyCashNeedFromBusiness) || 0,
      // Legacy field
      non_business_monthly_cash_needs: parseFloat(nonBusinessMonthlyCashNeeds) || 0,
      liquid_assets_return_rate: liquidAssetsReturnRate === '' ? 3 : parseFloat(liquidAssetsReturnRate),
      retirement_accounts_return_rate: retirementAccountsReturnRate === '' ? 7 : parseFloat(retirementAccountsReturnRate),
      real_estate_return_rate: realEstateReturnRate === '' ? 4 : parseFloat(realEstateReturnRate),
      other_investments_return_rate: otherInvestmentsReturnRate === '' ? 6 : parseFloat(otherInvestmentsReturnRate),
      liability_reduction_rate: liabilityReductionRate === '' ? 0 : parseFloat(liabilityReductionRate),
      // Post-exit return rates
      post_exit_liquid_assets_return_rate: postExitLiquidAssetsReturnRate === '' ? 3 : parseFloat(postExitLiquidAssetsReturnRate),
      post_exit_retirement_accounts_return_rate: postExitRetirementAccountsReturnRate === '' ? 7 : parseFloat(postExitRetirementAccountsReturnRate),
      post_exit_real_estate_return_rate: postExitRealEstateReturnRate === '' ? 4 : parseFloat(postExitRealEstateReturnRate),
      post_exit_other_investments_return_rate: postExitOtherInvestmentsReturnRate === '' ? 6 : parseFloat(postExitOtherInvestmentsReturnRate),
      post_exit_liability_reduction_rate: postExitLiabilityReductionRate === '' ? 0 : parseFloat(postExitLiabilityReductionRate),
      // Post-exit source category
      post_exit_source_category: postExitSourceCategory,
      // Earnout configuration
      earnout_years: parseInt(earnoutYears) || 5,
      earnout_interest_rate: parseFloat(earnoutInterestRate) || 5,
      earnout_tax_rate: parseFloat(earnoutTaxRate) || 42,
      earnout_payment_frequency: earnoutPaymentFrequency,
      // Lump sum configuration
      lump_sum_percent: parseFloat(lumpSumPercent) || 0,
      lump_sum_dollar: parseFloat(lumpSumDollar) || 0,
      // Lump sum allocation
      lump_sum_allocation_liquid_assets: parseFloat(lumpSumAllocationLiquidAssets) || 0,
      lump_sum_allocation_real_estate: parseFloat(lumpSumAllocationRealEstate) || 0,
      lump_sum_allocation_other_investments: parseFloat(lumpSumAllocationOtherInvestments) || 100,
      // Earnout allocation
      earnout_allocation_liquid_assets: parseFloat(earnoutAllocationLiquidAssets) || 0,
      earnout_allocation_real_estate: parseFloat(earnoutAllocationRealEstate) || 0,
      earnout_allocation_other_investments: parseFloat(earnoutAllocationOtherInvestments) || 100
    };
    onSave(data);
  };

  if (!show) return null;

  const calculatedNetWorth =
    (parseFloat(liquidAssets) || 0) +
    (parseFloat(retirementAccounts) || 0) +
    (parseFloat(realEstateEquity) || 0) +
    (parseFloat(otherInvestments) || 0) -
    (parseFloat(totalLiabilities) || 0);

  // Calculate future net worth at retirement
  const currentYear = new Date().getFullYear();
  const yearsUntilExit = Math.max(0, exitYear - currentYear);
  const curAge = parseInt(currentAge) || 50;
  const retAge = parseInt(retirementAge) || 65;
  const yearsUntilRetirement = Math.max(0, retAge - curAge);

  // Calculate monthly withdrawal (PMT) for the selected non-business source
  const monthlyWithdrawal = parseFloat(monthlyCashNeedFromNonBusiness) || 0;

  /**
   * Calculate FV with growing monthly withdrawals
   * Growing Annuity Formula: FV = PV(1+r)^n - PMT * [((1+r)^n - (1+g)^n) / (r - g)]
   * Where payments grow at rate g per period (monthly inflation)
   */
  const calculateFVwithPMT = (pv, annualRate, years, monthlyPmt, annualGrowthRate = 0) => {
    if (years === 0) return pv;
    const monthlyRate = annualRate / 12 / 100;
    const monthlyGrowthRate = annualGrowthRate / 12 / 100;
    const n = years * 12; // Total months

    // Calculate FV of the present value
    const fvPV = pv * Math.pow(1 + monthlyRate, n);

    // Calculate FV of growing withdrawals
    if (monthlyPmt === 0) {
      return fvPV;
    }

    // Handle edge case where return rate equals growth rate
    if (Math.abs(monthlyRate - monthlyGrowthRate) < 1e-10) {
      // When r = g: FV = PV(1+r)^n - PMT * n * (1+r)^(n-1)
      const fvPMT = monthlyPmt * n * Math.pow(1 + monthlyRate, n - 1);
      return fvPV - fvPMT;
    }

    // Normal case: Growing annuity formula
    const fvPMT = monthlyPmt * ((Math.pow(1 + monthlyRate, n) - Math.pow(1 + monthlyGrowthRate, n)) / (monthlyRate - monthlyGrowthRate));
    return fvPV - fvPMT;
  };

  // BUSINESS SALE PROCEEDS CALCULATIONS
  // Calculate future business value at exit
  const futureBusinessValue = (parseFloat(desiredSalePrice) || 0) * Math.pow(1 + (parseFloat(businessGrowthRate) || 0) / 100, yearsUntilExit);

  // Calculate gross proceeds (before tax)
  const ownershipPct = (parseFloat(ownershipPercentage) || 100) / 100;
  const taxRate = (taxRateOnSale === '' ? 42 : parseFloat(taxRateOnSale)) / 100;
  const grossProceeds = futureBusinessValue * ownershipPct;

  // Calculate lump sum amount (from gross proceeds, before tax)
  const lumpSumGross = lumpSumInputMode === 'percent'
    ? grossProceeds * (parseFloat(lumpSumPercent) || 0) / 100
    : parseFloat(lumpSumDollar) || 0;

  // Apply tax only to lump sum
  const lumpSumAmount = lumpSumGross * (1 - taxRate);

  // Calculate earnout amount (remaining gross proceeds)
  const earnoutGross = grossProceeds - lumpSumGross;
  const earnoutTaxRateDecimal = (parseFloat(earnoutTaxRate) || 42) / 100;

  // Calculate earnout annuity payment BEFORE tax (based on gross earnout)
  // Annuity formula: PMT = PV * [r * (1 + r)^n] / [(1 + r)^n - 1]
  const calculateAnnuityPayment = (presentValue, annualRate, years, frequency) => {
    if (years === 0 || presentValue === 0) return 0;
    const periodsPerYear = frequency === 'monthly' ? 12 : 1;
    const periodRate = annualRate / 100 / periodsPerYear;
    const totalPeriods = years * periodsPerYear;

    if (periodRate === 0) {
      // If no interest, simple division
      return presentValue / totalPeriods;
    }

    const numerator = periodRate * Math.pow(1 + periodRate, totalPeriods);
    const denominator = Math.pow(1 + periodRate, totalPeriods) - 1;
    return presentValue * (numerator / denominator);
  };

  // Calculate gross earnout payment (before tax)
  const earnoutPaymentGross = calculateAnnuityPayment(
    earnoutGross,
    parseFloat(earnoutInterestRate) || 0,
    parseFloat(earnoutYears) || 5,
    earnoutPaymentFrequency
  );

  // Apply tax to each earnout payment (tax paid as received)
  const earnoutPaymentAmount = earnoutPaymentGross * (1 - earnoutTaxRateDecimal);

  // Total after-tax earnout value (sum of all after-tax payments)
  const earnoutAmount = earnoutPaymentAmount * (parseFloat(earnoutYears) || 5) * (earnoutPaymentFrequency === 'monthly' ? 12 : 1);

  // Monthly earnout payment (for use as PMT in FV calculations)
  const monthlyEarnoutPayment = earnoutPaymentFrequency === 'monthly'
    ? earnoutPaymentAmount
    : earnoutPaymentAmount / 12;

  // Calculate future value for each asset type with PMT if it's the selected source
  // Use !== '' check to allow 0% returns (since 0 is falsy but valid)
  const liquidAssetsRate = liquidAssetsReturnRate !== '' ? parseFloat(liquidAssetsReturnRate) : 3;
  const annualInflationRate = parseFloat(inflationRate) || 0;
  const liquidAssetsFV = nonBusinessSourceCategory === 'liquid_assets'
    ? calculateFVwithPMT(parseFloat(liquidAssets) || 0, liquidAssetsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (parseFloat(liquidAssets) || 0) * Math.pow(1 + liquidAssetsRate / 100, yearsUntilExit);

  const retirementAccountsRate = retirementAccountsReturnRate !== '' ? parseFloat(retirementAccountsReturnRate) : 7;
  const retirementAccountsFV = nonBusinessSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(parseFloat(retirementAccounts) || 0, retirementAccountsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (parseFloat(retirementAccounts) || 0) * Math.pow(1 + retirementAccountsRate / 100, yearsUntilExit);

  const realEstateRate = realEstateReturnRate !== '' ? parseFloat(realEstateReturnRate) : 4;
  const realEstateFV = (nonBusinessSourceCategory === 'real_estate' || nonBusinessSourceCategory === 'real_estate_equity')
    ? calculateFVwithPMT(parseFloat(realEstateEquity) || 0, realEstateRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (parseFloat(realEstateEquity) || 0) * Math.pow(1 + realEstateRate / 100, yearsUntilExit);

  const otherInvestmentsRate = otherInvestmentsReturnRate !== '' ? parseFloat(otherInvestmentsReturnRate) : 6;
  // Other Investments base FV (lump sum will be allocated in post-exit calculations)
  const otherInvestmentsBaseFV = nonBusinessSourceCategory === 'other_investments'
    ? calculateFVwithPMT(parseFloat(otherInvestments) || 0, otherInvestmentsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (parseFloat(otherInvestments) || 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsUntilExit);

  // Use base FV - lump sum will be allocated to asset categories in post-exit calculations
  const otherInvestmentsFV = otherInvestmentsBaseFV;

  // Calculate future liabilities with reduction rate: FV = PV * (1 - reduction_rate)^n
  const futureLiabilities = (parseFloat(totalLiabilities) || 0) * Math.pow(1 - (parseFloat(liabilityReductionRate) || 0) / 100, yearsUntilExit);

  // Calculate future value of outside income at exit
  const outsideIncomeGrowthRate = (parseFloat(outsideIncomeAnnualIncrease) || 0) / 100;
  const futureOutsideIncomeMonthly = (parseFloat(outsideIncomeMonthly) || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilExit);

  // Calculate monthly need at exit with inflation and lifestyle changes
  const totalCurrentMonthlyNeed = parseFloat(monthlyCashNeed) || 0;
  const inflationMultiplier = Math.pow(1 + (parseFloat(inflationRate) || 2) / 100, yearsUntilExit);
  const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (parseFloat(lifestyleChangePercentage) || 0) / 100);

  // Post-exit monthly withdrawal is the net monthly need at exit (total minus outside income)
  // After exit, there's no more business income, so we need the full monthly need minus only outside income
  const postExitMonthlyWithdrawal = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

  // Sum all future values and subtract future liabilities
  const futureNetWorth = liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV - futureLiabilities;

  // Calculate non-business net worth at exit (before adding lump sum from business sale)
  const nonBusinessNetWorthAtExit = liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsBaseFV - futureLiabilities;

  // POST-EXIT CALCULATIONS (from exit year to life expectancy)
  const lifeExp = parseInt(lifeExpectancy) || 100;
  const yearsPostExit = Math.max(0, lifeExp - (curAge + yearsUntilExit));

  // Calculate monthly need at end of life expectancy (growing with inflation from exit)
  const monthlyNeedAtLifeExpectancy = postExitMonthlyWithdrawal * Math.pow(1 + (parseFloat(inflationRate) || 0) / 100, yearsPostExit);

  // Calculate lump sum and earnout allocations
  const lumpSumAllocLiquid = isNaN(parseFloat(lumpSumAllocationLiquidAssets)) ? 0 : parseFloat(lumpSumAllocationLiquidAssets);
  const lumpSumAllocRealEstate = isNaN(parseFloat(lumpSumAllocationRealEstate)) ? 0 : parseFloat(lumpSumAllocationRealEstate);
  const lumpSumAllocOtherInv = isNaN(parseFloat(lumpSumAllocationOtherInvestments)) ? 100 : parseFloat(lumpSumAllocationOtherInvestments);

  const earnoutAllocLiquid = isNaN(parseFloat(earnoutAllocationLiquidAssets)) ? 0 : parseFloat(earnoutAllocationLiquidAssets);
  const earnoutAllocRealEstate = isNaN(parseFloat(earnoutAllocationRealEstate)) ? 0 : parseFloat(earnoutAllocationRealEstate);
  const earnoutAllocOtherInv = isNaN(parseFloat(earnoutAllocationOtherInvestments)) ? 100 : parseFloat(earnoutAllocationOtherInvestments);

  // Calculate allocated amounts
  const lumpSumToLiquid = lumpSumAmount * (lumpSumAllocLiquid / 100);
  const lumpSumToRealEstate = lumpSumAmount * (lumpSumAllocRealEstate / 100);
  const lumpSumToOtherInv = lumpSumAmount * (lumpSumAllocOtherInv / 100);

  const monthlyEarnoutToLiquid = monthlyEarnoutPayment * (earnoutAllocLiquid / 100);
  const monthlyEarnoutToRealEstate = monthlyEarnoutPayment * (earnoutAllocRealEstate / 100);
  const monthlyEarnoutToOtherInv = monthlyEarnoutPayment * (earnoutAllocOtherInv / 100);

  // Earnout period calculations
  const earnoutPeriodYears = parseFloat(earnoutYears) || 5;
  const yearsWithEarnout = Math.min(yearsPostExit, earnoutPeriodYears);
  const yearsAfterEarnout = Math.max(0, yearsPostExit - earnoutPeriodYears);

  // Calculate post-exit FV using the FV at retirement as the new PV
  // Each asset category gets its allocated lump sum at exit and earnout during earnout period
  const postExitLiquidAssetsRate = postExitLiquidAssetsReturnRate !== '' ? parseFloat(postExitLiquidAssetsReturnRate) : 3;

  // Liquid Assets: Add lump sum at exit, then during earnout period add earnout payments
  const liquidPhase1Withdrawal = postExitSourceCategory === 'liquid_assets' ? postExitMonthlyWithdrawal : 0;
  const liquidNetPMTDuringEarnout = monthlyEarnoutToLiquid - liquidPhase1Withdrawal;
  const liquidAssetsFVAfterEarnout = calculateFVwithPMT(
    liquidAssetsFV + lumpSumToLiquid, // Add lump sum at exit
    postExitLiquidAssetsRate,
    yearsWithEarnout,
    -liquidNetPMTDuringEarnout,
    annualInflationRate
  );
  const liquidPhase2Withdrawal = postExitSourceCategory === 'liquid_assets' ? postExitMonthlyWithdrawal : 0;
  const liquidAssetsFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(liquidAssetsFVAfterEarnout, postExitLiquidAssetsRate, yearsAfterEarnout, liquidPhase2Withdrawal, annualInflationRate)
    : liquidAssetsFVAfterEarnout;

  const postExitRetirementAccountsRate = postExitRetirementAccountsReturnRate !== '' ? parseFloat(postExitRetirementAccountsReturnRate) : 7;

  // Retirement Accounts: No lump sum or earnout, only withdrawals
  const retirementAccountsFVEndOfLife = postExitSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(retirementAccountsFV, postExitRetirementAccountsRate, yearsPostExit, postExitMonthlyWithdrawal, annualInflationRate)
    : retirementAccountsFV * Math.pow(1 + postExitRetirementAccountsRate / 100, yearsPostExit);

  const postExitRealEstateRate = postExitRealEstateReturnRate !== '' ? parseFloat(postExitRealEstateReturnRate) : 4;

  // Real Estate: Add lump sum at exit, then during earnout period add earnout payments
  const realEstatePhase1Withdrawal = (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') ? postExitMonthlyWithdrawal : 0;
  const realEstateNetPMTDuringEarnout = monthlyEarnoutToRealEstate - realEstatePhase1Withdrawal;
  const realEstateFVAfterEarnout = calculateFVwithPMT(
    realEstateFV + lumpSumToRealEstate, // Add lump sum at exit
    postExitRealEstateRate,
    yearsWithEarnout,
    -realEstateNetPMTDuringEarnout,
    annualInflationRate
  );
  const realEstatePhase2Withdrawal = (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') ? postExitMonthlyWithdrawal : 0;
  const realEstateFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(realEstateFVAfterEarnout, postExitRealEstateRate, yearsAfterEarnout, realEstatePhase2Withdrawal, annualInflationRate)
    : realEstateFVAfterEarnout;

  const postExitOtherInvestmentsRate = postExitOtherInvestmentsReturnRate !== '' ? parseFloat(postExitOtherInvestmentsReturnRate) : 6;

  // Other Investments: Add lump sum at exit, then during earnout period add earnout payments
  const otherInvPhase1Withdrawal = postExitSourceCategory === 'other_investments' ? postExitMonthlyWithdrawal : 0;
  const otherInvNetPMTDuringEarnout = monthlyEarnoutToOtherInv - otherInvPhase1Withdrawal;
  const otherInvestmentsFVAfterEarnout = calculateFVwithPMT(
    otherInvestmentsFV + lumpSumToOtherInv, // Add lump sum at exit
    postExitOtherInvestmentsRate,
    yearsWithEarnout,
    -otherInvNetPMTDuringEarnout,
    annualInflationRate
  );
  const otherInvPhase2Withdrawal = postExitSourceCategory === 'other_investments' ? postExitMonthlyWithdrawal : 0;
  const otherInvestmentsFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(
        otherInvestmentsFVAfterEarnout,
        postExitOtherInvestmentsRate,
        yearsAfterEarnout,
        otherInvPhase2Withdrawal,
        annualInflationRate
      )
    : otherInvestmentsFVAfterEarnout;

  // Calculate future liabilities at end of life with post-exit reduction rate
  const postExitLiabilityRate = postExitLiabilityReductionRate !== '' ? parseFloat(postExitLiabilityReductionRate) : 0;
  const liabilitiesFVEndOfLife = futureLiabilities * Math.pow(1 - postExitLiabilityRate / 100, yearsPostExit);

  // Total net worth at end of life expectancy
  const netWorthAtEndOfLife = liquidAssetsFVEndOfLife + retirementAccountsFVEndOfLife + realEstateFVEndOfLife + otherInvestmentsFVEndOfLife - liabilitiesFVEndOfLife;

  // Calculate age at exit
  const ageAtExit = curAge + yearsUntilExit;

  // Calculate years in retirement (lifeExp already declared above on line 636)
  const yearsInRetirement = Math.max(0, lifeExp - retAge);

  // Calculate current net monthly need (after outside income)
  const currentMonthlyNeedAfterIncome = Math.max(0, totalCurrentMonthlyNeed - (parseFloat(outsideIncomeMonthly) || 0));

  // Calculate allocation validation
  const allocationValid = validateAllocation().valid;

  // Calculate display percentages for AllocationInputField
  const calculatedNonBusinessPercent = totalCurrentMonthlyNeed > 0 ? ((parseFloat(monthlyCashNeedFromNonBusiness) || 0) / totalCurrentMonthlyNeed * 100).toFixed(1) : '0';
  const calculatedBusinessPercent = totalCurrentMonthlyNeed > 0 ? ((parseFloat(monthlyCashNeedFromBusiness) || 0) / totalCurrentMonthlyNeed * 100).toFixed(1) : '0';
  const calculatedOutsideIncomePercent = totalCurrentMonthlyNeed > 0 ? ((parseFloat(outsideIncomeMonthly) || 0) / totalCurrentMonthlyNeed * 100).toFixed(1) : '0';

  // Format desired sale price for display (no decimals, with commas)
  const formattedDesiredSalePrice = desiredSalePrice
    ? Math.round(parseFloat(desiredSalePrice)).toLocaleString('en-US')
    : '';

  // Format net worth values for display (with commas, no decimals)
  const formatNetWorthValue = (value) => {
    return value ? Math.round(parseFloat(value)).toLocaleString('en-US') : '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 sm:p-6 border-b border-orange-700 z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <Target size={24} className="sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold truncate">Enhanced Wealth Gap Calculator</h2>
                <p className="text-orange-100 text-xs sm:text-sm hidden sm:block">Comprehensive financial exit planning</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-orange-200 transition p-2 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
              aria-label="Close"
            >
              <X size={24} className="sm:w-7 sm:h-7" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Wealth Goal Method Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">How would you like to calculate your wealth goal?</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setWealthGapMethod('single_number')}
                className={`p-4 rounded-xl border-2 transition text-left ${
                  wealthGapMethod === 'single_number'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <DollarSign size={24} className={wealthGapMethod === 'single_number' ? 'text-orange-600' : 'text-gray-400'} />
                  <h3 className="font-semibold text-gray-900">Single Number</h3>
                </div>
                <p className="text-sm text-gray-600">I know my total wealth goal</p>
              </button>

              <button
                onClick={() => setWealthGapMethod('monthly_needs')}
                className={`p-4 rounded-xl border-2 transition text-left ${
                  wealthGapMethod === 'monthly_needs'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <PiggyBank size={24} className={wealthGapMethod === 'monthly_needs' ? 'text-orange-600' : 'text-gray-400'} />
                  <h3 className="font-semibold text-gray-900">Monthly Needs (Recommended)</h3>
                </div>
                <p className="text-sm text-gray-600">Based on lifestyle and monthly income needs</p>
              </button>
            </div>
          </div>

          {/* Single Number Method */}
          {wealthGapMethod === 'single_number' && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Wealth Goal Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    value={formatNetWorthValue(wealthGoalAmount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || !isNaN(value)) {
                        setWealthGoalAmount(value);
                      }
                    }}
                    placeholder="5,000,000"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Total amount you need to retire comfortably</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Non-Business Net Worth <HelpTooltip content="Your total personal net worth excluding your business (assets minus liabilities)" />
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    value={formatNetWorthValue(nonBusinessNetWorth)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || !isNaN(value)) {
                        setNonBusinessNetWorth(value);
                      }
                    }}
                    placeholder="1,500,000"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Your current personal assets minus liabilities (excluding business value)</p>
              </div>
            </div>
          )}

          {/* Age & Timeline Section - Visible for both methods */}
          <div className="pt-6 border-t border-gray-200">
            <CollapsibleSection
              title="Age & Timeline"
              icon={<Calendar size={20} className="text-purple-600" />}
              color="purple"
              isExpanded={expandedSections.ageTimeline}
              onToggle={() => toggleSection('ageTimeline')}
            >
              <div className="space-y-3 mt-4">
                {/* All 6 fields in one row */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                      Current Age {businessProfile?.client_date_of_birth && <span className="text-purple-600">(Auto)</span>}
                      <HelpTooltip content="Your current age. Automatically populated from your client profile if date of birth is provided." />
                    </label>
                    <input
                      type="number"
                      value={currentAge}
                      onChange={(e) => setCurrentAge(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50"
                      readOnly={!!businessProfile?.client_date_of_birth}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                      Retirement Age
                      <HelpTooltip content="The age at which you plan to retire from your business. This determines how many years of income your investments need to provide." />
                    </label>
                    <input
                      type="number"
                      value={retirementAge}
                      onChange={(e) => setRetirementAge(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1">
                      Years to Retirement <span className="text-purple-600">(Auto)</span>
                    </label>
                    <input
                      type="number"
                      value={yearsToRetirement}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-purple-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1">
                      Exit Year <span className="text-purple-600">(Auto)</span>
                    </label>
                    <input
                      type="number"
                      value={exitYear}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-purple-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                      Life Expectancy
                      <HelpTooltip content="Expected age of longevity. Average life expectancy is around 80-85, but plan conservatively (90-100) to ensure you don't outlive your money." />
                    </label>
                    <input
                      type="number"
                      value={lifeExpectancy}
                      onChange={(e) => setLifeExpectancy(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                      Years in Retirement <span className="text-purple-600">(Auto)</span>
                      <HelpTooltip content="Calculated as Life Expectancy minus Retirement Age. This is the number of years your retirement savings need to last." />
                    </label>
                    <input
                      type="number"
                      value={yearsInRetirement}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-purple-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* Monthly Needs Method */}
          {wealthGapMethod === 'monthly_needs' && (
            <div className="space-y-6">
              {/* Current Lifestyle Section */}
              <CollapsibleSection
                title="Current Lifestyle"
                icon={<DollarSign size={20} className="text-blue-600" />}
                color="blue"
                isExpanded={expandedSections.lifestyle}
                onToggle={() => toggleSection('lifestyle')}
              >
                {/* Total Monthly Cash Need with Lifestyle & Inflation Sliders */}
                <div className="mt-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Total Monthly Cash Need */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1">
                        Total Current Monthly Cash Need <HelpTooltip content="Total amount you need per month to maintain your current lifestyle. The fields below must add up to this amount." />
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                        <input
                          type="text"
                          value={formatNetWorthValue(monthlyCashNeed)}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '');
                            if (value === '' || !isNaN(value)) {
                              setMonthlyCashNeed(value);
                            }
                          }}
                          placeholder="15,000"
                          className="w-full pl-7 pr-3 py-2 border-2 border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold text-blue-900"
                        />
                      </div>
                    </div>

                    {/* Lifestyle Change at Exit Slider */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        Lifestyle Change at Exit: <span className="font-bold text-blue-700">{lifestyleChangePercentage}%</span>
                        <HelpTooltip content="Expected percentage change in spending when you exit. Positive values mean increased spending, negative values mean decreased spending." />
                      </label>
                      <input
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={lifestyleChangePercentage}
                        onChange={(e) => setLifestyleChangePercentage(e.target.value)}
                        className="w-full h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer slider-thumb mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>-50%</span>
                        <span>0%</span>
                        <span>+50%</span>
                      </div>
                    </div>

                    {/* Inflation Rate Slider */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        Inflation Rate: <span className="font-bold text-blue-700">{inflationRate}%</span>
                        <HelpTooltip content="Expected annual inflation rate. Historical average is 2-3%." />
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="0.1"
                        value={inflationRate}
                        onChange={(e) => setInflationRate(e.target.value)}
                        className="w-full h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer slider-thumb mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Cash Flow Allocation Section */}
                <div className="mt-6 pt-4 border-t border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-900">Monthly Cash Need Allocation</h4>
                    <div className="flex items-center gap-4">
                      {/* Remaining Allocation Indicator */}
                      {totalCurrentMonthlyNeed > 0 && (() => {
                        const totalAllocated = (parseFloat(monthlyCashNeedFromNonBusiness) || 0) +
                                              (parseFloat(monthlyCashNeedFromBusiness) || 0) +
                                              (parseFloat(outsideIncomeMonthly) || 0);
                        const remaining = totalCurrentMonthlyNeed - totalAllocated;
                        const remainingPercent = ((remaining / totalCurrentMonthlyNeed) * 100).toFixed(1);

                        if (Math.abs(remaining) > 1) {
                          return (
                            <div className="text-right bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200">
                              <div className="text-xs text-orange-700 font-semibold">
                                Remaining: {remaining > 0 ? formatCurrency(remaining) : `-${formatCurrency(Math.abs(remaining))}`} <span className="text-orange-600">({remainingPercent > 0 ? `${remainingPercent}%` : `${remainingPercent}%`})</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {!allocationValid && totalCurrentMonthlyNeed > 0 && (
                        <span className="text-xs text-red-600 font-semibold bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
                          Must equal 100%
                        </span>
                      )}
                      {allocationValid && totalCurrentMonthlyNeed > 0 && (
                        <span className="text-xs text-green-600 font-semibold bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
                          ✓ Valid (100%)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Non-Business Net Worth */}
                    <div className="space-y-3">
                      <AllocationInputField
                        label="From Non-Business Net Worth"
                        dollarValue={monthlyCashNeedFromNonBusiness}
                        onDollarChange={setMonthlyCashNeedFromNonBusiness}
                        percentValue={nonBusinessPercent}
                        onPercentChange={setNonBusinessPercent}
                        totalAmount={monthlyCashNeed}
                        inputMode={nonBusinessInputMode}
                        onToggleMode={() => {
                          const newMode = nonBusinessInputMode === 'dollar' ? 'percent' : 'dollar';
                          setNonBusinessInputMode(newMode);
                          // Sync percent value when switching to percent mode
                          if (newMode === 'percent') {
                            const totalNeed = parseFloat(monthlyCashNeed) || 0;
                            const dollarVal = parseFloat(monthlyCashNeedFromNonBusiness) || 0;
                            const percent = totalNeed > 0 ? ((dollarVal / totalNeed) * 100).toFixed(1) : '0';
                            setNonBusinessPercent(percent);
                          }
                        }}
                        placeholder="0"
                        helpText="Monthly cash needs currently met from your personal assets (savings, investments, real estate equity, etc.). Select which asset category will be used to fund these needs."
                      />

                      {/* Asset Category Selector */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                          Source Category
                          <HelpTooltip content="Which asset category will be used to fund your non-business cash needs. Monthly withdrawals will deplete this specific asset category over time." />
                        </label>
                        <select
                          value={nonBusinessSourceCategory}
                          onChange={(e) => setNonBusinessSourceCategory(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="liquid_assets">Liquid Assets</option>
                          <option value="retirement_accounts">Retirement Accounts</option>
                          <option value="real_estate_equity">Real Estate Equity</option>
                          <option value="other_investments">Other Investments</option>
                        </select>
                      </div>

                      {/* Post-Exit Source Category Selector */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                          Post-Exit Source Category
                          <HelpTooltip content="Which asset category will be used to fund your monthly cash needs AFTER exit (during retirement). This determines which asset is depleted by withdrawals from exit year through end of life expectancy." />
                        </label>
                        <select
                          value={postExitSourceCategory}
                          onChange={(e) => setPostExitSourceCategory(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="liquid_assets">Liquid Assets</option>
                          <option value="retirement_accounts">Retirement Accounts</option>
                          <option value="real_estate_equity">Real Estate Equity</option>
                          <option value="other_investments">Other Investments</option>
                        </select>
                      </div>
                    </div>

                    {/* From Business */}
                    <AllocationInputField
                      label="From Business (Owner's Draw/Salary)"
                      dollarValue={monthlyCashNeedFromBusiness}
                      onDollarChange={setMonthlyCashNeedFromBusiness}
                      percentValue={businessPercent}
                      onPercentChange={setBusinessPercent}
                      totalAmount={monthlyCashNeed}
                      inputMode={businessInputMode}
                      onToggleMode={() => {
                        const newMode = businessInputMode === 'dollar' ? 'percent' : 'dollar';
                        setBusinessInputMode(newMode);
                        // Sync percent value when switching to percent mode
                        if (newMode === 'percent') {
                          const totalNeed = parseFloat(monthlyCashNeed) || 0;
                          const dollarVal = parseFloat(monthlyCashNeedFromBusiness) || 0;
                          const percent = totalNeed > 0 ? ((dollarVal / totalNeed) * 100).toFixed(1) : '0';
                          setBusinessPercent(percent);
                        }
                      }}
                      placeholder="0"
                      helpText="Monthly cash needs currently met from your business income (owner's draw, salary, distributions). This income will stop when you exit the business."
                    />

                    {/* Outside Income with Growth Rate */}
                    <div className="space-y-3">
                      <AllocationInputField
                        label="Outside Income Sources"
                        dollarValue={outsideIncomeMonthly}
                        onDollarChange={setOutsideIncomeMonthly}
                        percentValue={outsideIncomePercent}
                        onPercentChange={setOutsideIncomePercent}
                        totalAmount={monthlyCashNeed}
                        inputMode={outsideIncomeInputMode}
                        onToggleMode={() => {
                          const newMode = outsideIncomeInputMode === 'dollar' ? 'percent' : 'dollar';
                          setOutsideIncomeInputMode(newMode);
                          // Sync percent value when switching to percent mode
                          if (newMode === 'percent') {
                            const totalNeed = parseFloat(monthlyCashNeed) || 0;
                            const dollarVal = parseFloat(outsideIncomeMonthly) || 0;
                            const percent = totalNeed > 0 ? ((dollarVal / totalNeed) * 100).toFixed(1) : '0';
                            setOutsideIncomePercent(percent);
                          }
                        }}
                        placeholder="0"
                        helpText="Monthly cash needs met from sources outside your business (Social Security, pensions, rental income, spouse's income, etc.). These sources will continue after exit."
                      />

                      {/* Outside Income Growth Rate */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                          Annual Growth Rate: <span className="font-bold text-blue-700">{outsideIncomeAnnualIncrease}%</span>
                          <HelpTooltip content="Expected annual growth rate of your outside income sources (e.g., Social Security COLAs, pension increases)." />
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="0.1"
                          value={outsideIncomeAnnualIncrease}
                          onChange={(e) => setOutsideIncomeAnnualIncrease(e.target.value)}
                          className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                        />
                      </div>

                      {/* Display Future Value of Outside Income */}
                      {outsideIncomeMonthly && yearsUntilExit > 0 && (
                        <div className="bg-green-50 border border-green-300 rounded-lg p-2">
                          <div className="text-xs text-gray-700 mb-1">At Exit ({exitYear}):</div>
                          <div className="text-sm font-bold text-green-700">
                            {formatCurrency(futureOutsideIncomeMonthly)}/month
                          </div>
                          <div className="text-xs text-green-600 mt-0.5">
                            {yearsUntilExit} years @ {outsideIncomeAnnualIncrease}% growth
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Monthly Need at Exit Summary */}
                {monthlyCashNeed && (
                  <div className="mt-6 pt-4 border-t border-blue-200">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-gray-900 mb-3">Projected Monthly Need at Exit</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <div className="text-2xl font-bold text-blue-700 mb-2">
                            {formatCurrency(monthlyNeedAtExit)}
                            <span className="text-sm text-gray-600 font-normal"> / month</span>
                          </div>
                          <div className="text-xs text-gray-700 space-y-1">
                            <div className="flex justify-between">
                              <span>Current monthly need:</span>
                              <span className="font-semibold">{formatCurrency(parseFloat(monthlyCashNeed) || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Years until exit:</span>
                              <span className="font-semibold">{yearsUntilExit} years</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Inflation rate:</span>
                              <span className="font-semibold">{inflationRate}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Lifestyle change:</span>
                              <span className="font-semibold">{lifestyleChangePercentage > 0 ? '+' : ''}{lifestyleChangePercentage}%</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="text-xs text-gray-600 leading-relaxed">
                            <p className="mb-2">
                              <strong>Calculation:</strong> Your current monthly need of {formatCurrency(parseFloat(monthlyCashNeed) || 0)} is projected forward {yearsUntilExit} years with {inflationRate}% annual inflation, then adjusted by {lifestyleChangePercentage > 0 ? '+' : ''}{lifestyleChangePercentage}% for lifestyle changes at exit.
                            </p>
                            <p className="text-blue-700 font-semibold">
                              This is the estimated amount you'll need per month when you exit in {exitYear}.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Net Monthly Need at Exit (After Outside Income) */}
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-gray-900 mb-3">Net Monthly Need at Exit (Post-Exit Withdrawal)</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <div className="text-2xl font-bold text-emerald-700 mb-2">
                            {formatCurrency(postExitMonthlyWithdrawal)}
                            <span className="text-sm text-gray-600 font-normal"> / month</span>
                          </div>
                          <div className="text-xs text-gray-700 space-y-1">
                            <div className="flex justify-between">
                              <span>Monthly need at exit:</span>
                              <span className="font-semibold">{formatCurrency(monthlyNeedAtExit)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Outside income at exit:</span>
                              <span className="font-semibold">-{formatCurrency(futureOutsideIncomeMonthly)}</span>
                            </div>
                            <div className="flex justify-between border-t border-gray-300 pt-1">
                              <span className="font-bold">Net from assets:</span>
                              <span className="font-bold text-emerald-700">{formatCurrency(postExitMonthlyWithdrawal)}</span>
                            </div>
                            <div className="flex justify-between mt-2 pt-1 border-t border-gray-200">
                              <span className="text-xs italic text-gray-600">At life expectancy (Age {lifeExp}):</span>
                              <span className="text-xs font-semibold text-blue-600">{formatCurrency(monthlyNeedAtLifeExpectancy)}/mo</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="text-xs text-gray-600 leading-relaxed">
                            <p className="mb-2">
                              <strong>Post-Exit Calculation:</strong> After you exit, there's no more business income. Your monthly need of {formatCurrency(monthlyNeedAtExit)} minus outside income sources of {formatCurrency(futureOutsideIncomeMonthly)} equals the net amount that must come from your assets.
                            </p>
                            <p className="text-emerald-700 font-semibold">
                              This is the monthly withdrawal amount applied to your selected post-exit asset category from exit through end of life expectancy.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Capital Needed Calculation Breakdown - Powered by Backend API */}
                    {postExitMonthlyWithdrawal > 0 && (
                      <div className="mt-4 bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-gray-900">Total Capital Needed</h4>
                          {isCalculating && (
                            <span className="text-xs text-gray-600 italic">Calculating...</span>
                          )}
                        </div>

                        {/* Annual Return Rate Slider */}
                        <div className="mb-4 bg-white border border-orange-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-bold text-gray-900">
                              Annual Return Rate (Post-Exit)
                            </label>
                            <span className="text-lg font-bold text-blue-600">{returnRate}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="15"
                            step="0.5"
                            value={returnRate}
                            onChange={(e) => setReturnRate(e.target.value)}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right,
                                #ef4444 0%,
                                #f59e0b ${(parseFloat(returnRate) / 15 * 50)}%,
                                #10b981 ${(parseFloat(returnRate) / 15 * 100)}%,
                                #e5e7eb ${(parseFloat(returnRate) / 15 * 100)}%)`,
                              WebkitAppearance: 'none'
                            }}
                          />
                          <div className="flex justify-between text-xs text-gray-600 mt-1">
                            <span>0%</span>
                            <span className="text-gray-500">Conservative</span>
                            <span className="text-gray-500">Moderate</span>
                            <span className="text-gray-500">Aggressive</span>
                            <span>15%</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            Adjust the expected annual return on your investments during retirement.
                            Higher returns reduce the capital needed, but come with higher risk.
                          </p>
                        </div>

                        {calculatedData?.required_sale_price_data ? (
                          <div className="space-y-3 text-xs">
                            {/* Summary Result */}
                            <div className="bg-white border border-orange-200 rounded p-3">
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-gray-900 text-sm">Total Capital Needed at Exit:</span>
                                <span className="text-2xl font-bold text-orange-700">
                                  {formatCurrency(calculatedData.required_sale_price_data.total_capital_needed)}
                                </span>
                              </div>
                              <div className="text-gray-600 text-xs mt-2">
                                This is the lump sum needed at your business exit to fund {formatCurrency(postExitMonthlyWithdrawal)}/month
                                (growing with inflation) for {calculatedData.required_sale_price_data.years_in_retirement} years in retirement,
                                assuming {returnRate}% annual investment returns.
                              </div>
                            </div>

                            {/* Key Breakdown Values */}
                            <div className="bg-white border border-orange-200 rounded p-3">
                              <div className="font-bold text-gray-900 mb-2">Calculation Details:</div>
                              <div className="space-y-1 text-gray-700">
                                <div className="flex justify-between">
                                  <span>Net Monthly Need:</span>
                                  <span className="font-semibold">{formatCurrency(calculatedData.required_sale_price_data.net_monthly_need)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Years in Retirement:</span>
                                  <span className="font-semibold">{calculatedData.required_sale_price_data.years_in_retirement} years</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Annual Return Rate:</span>
                                  <span className="font-semibold">{returnRate}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Annual Inflation Rate:</span>
                                  <span className="font-semibold">{inflationRate}%</span>
                                </div>
                              </div>
                            </div>

                            {/* Additional Info */}
                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                              <div className="font-bold text-gray-900 mb-2">Note:</div>
                              <div className="text-gray-700">
                                This calculation uses a growing annuity formula with proper monthly compounding.
                                The monthly payments grow with inflation while being discounted at the investment return rate.
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-600 text-sm text-center py-4">
                            {isCalculating ? 'Calculating...' : 'Enter values above to see calculation breakdown'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleSection>

              {/* Business Sale Parameters Section */}
              <CollapsibleSection
                title="Business Sale Parameters"
                icon={<Target size={20} className="text-yellow-600" />}
                color="yellow"
                isExpanded={expandedSections.businessSale}
                onToggle={() => toggleSection('businessSale')}
              >
                <div className="space-y-3 mt-4">
                  {/* Row 1: Current Value, Business Growth Rate, Future Value - 3 columns */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        Estimated Current Exit Value <span className="text-yellow-600">(Auto)</span>
                        <HelpTooltip content="Automatically populated from your Profit Gap current valuation or Value Gap current exit value. This is your best estimate of what your business is worth today." />
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                        <input
                          type="text"
                          value={formattedDesiredSalePrice}
                          readOnly
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-yellow-50 text-gray-700 cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        Business Growth Rate: <span className="font-bold text-yellow-700">{businessGrowthRate}%</span>
                        <HelpTooltip content="Expected annual growth rate of your business value from now until exit. Conservative estimates are typically 0-5%." />
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        step="0.5"
                        value={businessGrowthRate}
                        onChange={(e) => setBusinessGrowthRate(e.target.value)}
                        className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb mt-2"
                      />
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex flex-col justify-center">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-700">Future Value at Exit ({exitYear}):</span>
                        <span className="text-lg font-bold text-green-700">{formatCurrency(futureBusinessValue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Ownership and Estimated Sale Proceeds - 2 columns */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        Ownership: <span className="font-bold text-yellow-700">{ownershipPercentage}%</span>
                        <HelpTooltip content="Your ownership percentage in the business. If you're the sole owner, this is 100%. For partnerships, enter your share." />
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={ownershipPercentage}
                        onChange={(e) => setOwnershipPercentage(e.target.value)}
                        className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb mt-2"
                      />
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-700">Estimated Sale Proceeds (Gross):</span>
                        <span className="text-lg font-bold text-green-700">{formatCurrency(grossProceeds)}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Future Business Value: {formatCurrency(futureBusinessValue)} × {ownershipPercentage}% ownership
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Tax ({taxRateOnSale}%) applied separately to lump sum and earnout portions
                      </p>
                    </div>
                  </div>

                  {/* Sale Proceeds Allocation */}
                  <div className="mt-6 pt-4 border-t-2 border-yellow-200">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">Sale Proceeds Allocation</h4>

                    {/* Lump Sum Allocation - 3 Columns: Input, Tax Rate Slider, After-Tax */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                      <AllocationInputField
                        label="Lump Sum at Closing (Gross, Before Tax)"
                        dollarValue={lumpSumDollar}
                        onDollarChange={(val) => {
                          setLumpSumDollar(val);
                          // Only update percent when in dollar mode (prevent circular updates)
                          if (lumpSumInputMode === 'dollar' && grossProceeds > 0) {
                            const pct = ((parseFloat(val) || 0) / grossProceeds * 100).toFixed(1);
                            setLumpSumPercent(pct);
                          }
                        }}
                        percentValue={lumpSumPercent}
                        onPercentChange={(val) => {
                          setLumpSumPercent(val);
                          // Update dollar when percent changes (only in percent mode)
                          if (lumpSumInputMode === 'percent') {
                            const dollar = (grossProceeds * (parseFloat(val) || 0) / 100).toString();
                            setLumpSumDollar(dollar);
                          }
                        }}
                        totalAmount={grossProceeds.toString()}
                        inputMode={lumpSumInputMode}
                        onToggleMode={() => {
                          const newMode = lumpSumInputMode === 'dollar' ? 'percent' : 'dollar';
                          setLumpSumInputMode(newMode);
                          // Sync values when switching modes
                          if (newMode === 'percent' && grossProceeds > 0) {
                            const pct = ((parseFloat(lumpSumDollar) || 0) / grossProceeds * 100).toFixed(1);
                            setLumpSumPercent(pct);
                          } else if (newMode === 'dollar') {
                            const dollar = (grossProceeds * (parseFloat(lumpSumPercent) || 0) / 100).toString();
                            setLumpSumDollar(dollar);
                          }
                        }}
                        placeholder="0"
                        helpText="Gross amount received at closing (before tax). After-tax amount will be added to Other Investments at exit year."
                      />

                      {/* Effective Tax Rate Slider - Middle Column */}
                      <div className="flex flex-col justify-center">
                        <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                          Effective Tax Rate: <span className="text-blue-600">{taxRateOnSale}%</span>
                          <HelpTooltip content="Combined effective tax rate applied to sale proceeds. Consider federal capital gains, state taxes, net investment income tax, and any additional taxes applicable to your situation." />
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          step="0.5"
                          value={taxRateOnSale}
                          onChange={(e) => setTaxRateOnSale(e.target.value)}
                          className="w-full h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>0%</span>
                          <span>25%</span>
                          <span>50%</span>
                        </div>
                      </div>

                      {/* Lump Sum After-Tax Display */}
                      {lumpSumGross > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded p-2 flex flex-col justify-center">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-600">After Tax ({taxRateOnSale}%):</span>
                            <span className="text-lg font-semibold text-green-700">{formatCurrency(lumpSumAmount)}</span>
                          </div>
                          <p className="text-xs text-gray-500">Allocated across asset categories below</p>
                        </div>
                      )}
                    </div>

                    {/* Lump Sum Allocation */}
                    {lumpSumAmount > 0 && (
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                        <h5 className="text-xs font-bold text-gray-900 mb-2">Lump Sum Allocation (After-Tax)</h5>
                        <p className="text-xs text-gray-600 mb-3">
                          Allocate {formatCurrency(lumpSumAmount)} across asset categories (must sum to 100%)
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Liquid Assets: <span className="font-bold text-green-700">{lumpSumAllocationLiquidAssets}%</span>
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={lumpSumAllocationLiquidAssets}
                              onChange={(e) => setLumpSumAllocationLiquidAssets(e.target.value)}
                              className="w-full h-2 bg-gradient-to-r from-gray-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                            />
                            <p className="text-xs text-gray-500 mt-1">{formatCurrency(lumpSumAmount * parseFloat(lumpSumAllocationLiquidAssets) / 100)}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Real Estate: <span className="font-bold text-green-700">{lumpSumAllocationRealEstate}%</span>
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={lumpSumAllocationRealEstate}
                              onChange={(e) => setLumpSumAllocationRealEstate(e.target.value)}
                              className="w-full h-2 bg-gradient-to-r from-gray-400 to-blue-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                            />
                            <p className="text-xs text-gray-500 mt-1">{formatCurrency(lumpSumAmount * parseFloat(lumpSumAllocationRealEstate) / 100)}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Other Investments: <span className="font-bold text-green-700">{lumpSumAllocationOtherInvestments}%</span>
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={lumpSumAllocationOtherInvestments}
                              onChange={(e) => setLumpSumAllocationOtherInvestments(e.target.value)}
                              className="w-full h-2 bg-gradient-to-r from-gray-400 to-purple-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                            />
                            <p className="text-xs text-gray-500 mt-1">{formatCurrency(lumpSumAmount * parseFloat(lumpSumAllocationOtherInvestments) / 100)}</p>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-green-300">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-gray-700">Total Allocation:</span>
                            <span className={`text-sm font-bold ${(parseFloat(lumpSumAllocationLiquidAssets) + parseFloat(lumpSumAllocationRealEstate) + parseFloat(lumpSumAllocationOtherInvestments)) === 100 ? 'text-green-700' : 'text-red-700'}`}>
                              {(parseFloat(lumpSumAllocationLiquidAssets) + parseFloat(lumpSumAllocationRealEstate) + parseFloat(lumpSumAllocationOtherInvestments)).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3 mt-3">

                      {/* Earnout Section */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="space-y-1 mb-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-gray-900">Earnout Total (Gross):</span>
                            <span className="text-sm font-bold text-blue-700">{formatCurrency(earnoutGross)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">{earnoutPaymentFrequency === 'monthly' ? 'Monthly' : 'Annual'} Payment (Gross):</span>
                            <span className="text-sm font-semibold text-gray-700">{formatCurrency(earnoutPaymentGross)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">{earnoutPaymentFrequency === 'monthly' ? 'Monthly' : 'Annual'} Payment After Tax ({earnoutTaxRate}%):</span>
                            <span className="text-sm font-semibold text-green-700">{formatCurrency(earnoutPaymentAmount)}</span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* Single Row: All Earnout Controls */}
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                            {/* Earnout Years */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Earnout Period: <span className="font-bold text-blue-700">{earnoutYears} yrs</span>
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="15"
                                step="1"
                                value={earnoutYears}
                                onChange={(e) => setEarnoutYears(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                            </div>

                            {/* Interest Rate */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Interest Rate: <span className="font-bold text-blue-700">{earnoutInterestRate}%</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="15"
                                step="0.5"
                                value={earnoutInterestRate}
                                onChange={(e) => setEarnoutInterestRate(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                            </div>

                            {/* Earnout Tax Rate */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Earnout Effective Tax Rate: <span className="font-bold text-blue-700">{earnoutTaxRate}%</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="60"
                                step="0.5"
                                value={earnoutTaxRate}
                                onChange={(e) => setEarnoutTaxRate(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                            </div>

                            {/* Payment Frequency */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Payment Frequency</label>
                              <select
                                value={earnoutPaymentFrequency}
                                onChange={(e) => setEarnoutPaymentFrequency(e.target.value)}
                                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="annual">Annual</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Payment Amount Display */}
                        <div className="mt-3 pt-3 border-t border-blue-300">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-gray-700">
                              {earnoutPaymentFrequency === 'monthly' ? 'Monthly' : 'Annual'} Payment:
                            </span>
                            <span className="text-sm font-bold text-green-700">{formatCurrency(earnoutPaymentAmount)}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Allocated across asset categories below for {earnoutYears} years
                          </p>
                        </div>
                      </div>

                      {/* Earnout Allocation */}
                      {earnoutPaymentAmount > 0 && (
                        <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h5 className="text-xs font-bold text-gray-900 mb-2">Earnout Allocation (After-Tax)</h5>
                          <p className="text-xs text-gray-600 mb-3">
                            Allocate {earnoutPaymentFrequency === 'monthly' ? 'monthly' : 'annual'} payments across asset categories (must sum to 100%)
                          </p>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Liquid Assets: <span className="font-bold text-purple-700">{earnoutAllocationLiquidAssets}%</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={earnoutAllocationLiquidAssets}
                                onChange={(e) => setEarnoutAllocationLiquidAssets(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-gray-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                              <p className="text-xs text-gray-500 mt-1">{formatCurrency(earnoutPaymentAmount * parseFloat(earnoutAllocationLiquidAssets) / 100)}</p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Real Estate: <span className="font-bold text-purple-700">{earnoutAllocationRealEstate}%</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={earnoutAllocationRealEstate}
                                onChange={(e) => setEarnoutAllocationRealEstate(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-gray-400 to-blue-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                              <p className="text-xs text-gray-500 mt-1">{formatCurrency(earnoutPaymentAmount * parseFloat(earnoutAllocationRealEstate) / 100)}</p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Other Investments: <span className="font-bold text-purple-700">{earnoutAllocationOtherInvestments}%</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={earnoutAllocationOtherInvestments}
                                onChange={(e) => setEarnoutAllocationOtherInvestments(e.target.value)}
                                className="w-full h-2 bg-gradient-to-r from-gray-400 to-purple-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                              />
                              <p className="text-xs text-gray-500 mt-1">{formatCurrency(earnoutPaymentAmount * parseFloat(earnoutAllocationOtherInvestments) / 100)}</p>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t border-purple-300">
                            <div className="flex justify-between items-center">
                              <span className="text-xs font-semibold text-gray-700">Total Allocation:</span>
                              <span className={`text-sm font-bold ${(parseFloat(earnoutAllocationLiquidAssets) + parseFloat(earnoutAllocationRealEstate) + parseFloat(earnoutAllocationOtherInvestments)) === 100 ? 'text-green-700' : 'text-red-700'}`}>
                                {(parseFloat(earnoutAllocationLiquidAssets) + parseFloat(earnoutAllocationRealEstate) + parseFloat(earnoutAllocationOtherInvestments)).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            </div>
          )}

          {/* Current Net Worth - Only show for Monthly Needs method */}
          {wealthGapMethod === 'monthly_needs' && (
          <div className="pt-6 border-t border-gray-200">
            <CollapsibleSection
              title="Current Non-Business Financial Position & Post Business Exit Financial Position"
              icon={<DollarSign size={20} className="text-gray-600" />}
              color="gray"
              isExpanded={expandedSections.currentFinancial}
              onToggle={() => toggleSection('currentFinancial')}
            >
            <div className="space-y-3 mt-4">
              {/* Header Row */}
              <div className="grid grid-cols-12 gap-2 items-center pb-2 border-b-2 border-gray-300">
                <div className="col-span-12 lg:col-span-2"></div>
                <div className="col-span-12 lg:col-span-2 text-center">
                  <span className="text-xs font-bold text-gray-700">Current Amount</span>
                </div>
                <div className="col-span-12 lg:col-span-2 text-center">
                  <span className="text-xs font-bold text-blue-700">Pre-Exit Return</span>
                </div>
                <div className="col-span-12 lg:col-span-2 text-center">
                  <span className="text-xs font-bold text-emerald-700">FV at Exit (Age {retAge})</span>
                </div>
                <div className="col-span-12 lg:col-span-2 text-center">
                  <span className="text-xs font-bold text-purple-700">Post-Exit Return</span>
                </div>
                <div className="col-span-12 lg:col-span-2 text-center">
                  <span className="text-xs font-bold text-orange-700">FV at Age {lifeExpectancy}</span>
                </div>
              </div>

              {/* Liquid Assets */}
              <div className={`grid grid-cols-12 gap-2 items-center ${nonBusinessSourceCategory === 'liquid_assets' ? 'bg-purple-50 p-3 rounded-lg border-2 border-purple-300' : 'py-2'}`}>
                <div className="col-span-12 lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-900 flex items-center gap-1">
                    Liquid Assets
                    <HelpTooltip content="Cash, savings, money market accounts, and other easily accessible funds. Typical return: 1-4% annually." />
                  </label>
                  {nonBusinessSourceCategory === 'liquid_assets' && monthlyWithdrawal > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs font-bold text-purple-700">-{formatCurrency(monthlyWithdrawal)}/mo</span>
                    </div>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="relative">
                    <span className="absolute left-2 top-2 text-gray-500 text-xs">$</span>
                    <input
                      type="text"
                      value={formatNetWorthValue(liquidAssets)}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || !isNaN(value)) {
                          setLiquidAssets(value);
                        }
                      }}
                      placeholder="250,000"
                      className="w-full pl-6 pr-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={liquidAssetsReturnRate}
                      onChange={(e) => setLiquidAssetsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-blue-700 text-center">{liquidAssetsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-emerald-600 block">{formatCurrency(liquidAssetsFV)}</span>
                    {nonBusinessSourceCategory === 'liquid_assets' && monthlyWithdrawal > 0 && (
                      <span className="text-xs text-purple-600">After withdrawals</span>
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={postExitLiquidAssetsReturnRate}
                      onChange={(e) => setPostExitLiquidAssetsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-purple-700 text-center">{postExitLiquidAssetsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-orange-600 block">{formatCurrency(liquidAssetsFVEndOfLife)}</span>
                    {(lumpSumAmount > 0 && lumpSumAllocLiquid > 0) || (monthlyEarnoutPayment > 0 && earnoutAllocLiquid > 0) ? (
                      <span className="text-xs text-green-600">
                        {lumpSumAmount > 0 && lumpSumAllocLiquid > 0 && `+${lumpSumAllocLiquid}% lump`}
                        {lumpSumAmount > 0 && lumpSumAllocLiquid > 0 && monthlyEarnoutPayment > 0 && earnoutAllocLiquid > 0 && ', '}
                        {monthlyEarnoutPayment > 0 && earnoutAllocLiquid > 0 && `+${earnoutAllocLiquid}% earnout`}
                      </span>
                    ) : postExitSourceCategory === 'liquid_assets' && postExitMonthlyWithdrawal > 0 ? (
                      <span className="text-xs text-purple-600">-{formatCurrency(postExitMonthlyWithdrawal)}/mo</span>
                    ) : (
                      <span className="text-xs text-gray-500">{yearsPostExit} yrs</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Retirement Accounts */}
              <div className={`grid grid-cols-12 gap-2 items-center ${nonBusinessSourceCategory === 'retirement_accounts' ? 'bg-purple-50 p-3 rounded-lg border-2 border-purple-300' : 'py-2'}`}>
                <div className="col-span-12 lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-900 flex items-center gap-1">
                    Retirement Accounts
                    <HelpTooltip content="401(k), IRA, Roth IRA, and other tax-advantaged retirement accounts. Typical return: 6-8% annually for diversified portfolios." />
                  </label>
                  {nonBusinessSourceCategory === 'retirement_accounts' && monthlyWithdrawal > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs font-bold text-purple-700">-{formatCurrency(monthlyWithdrawal)}/mo</span>
                    </div>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="relative">
                    <span className="absolute left-2 top-2 text-gray-500 text-xs">$</span>
                    <input
                      type="text"
                      value={formatNetWorthValue(retirementAccounts)}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || !isNaN(value)) {
                          setRetirementAccounts(value);
                        }
                      }}
                      placeholder="500,000"
                      className="w-full pl-6 pr-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="15"
                      step="0.1"
                      value={retirementAccountsReturnRate}
                      onChange={(e) => setRetirementAccountsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-blue-700 text-center">{retirementAccountsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-emerald-600 block">{formatCurrency(retirementAccountsFV)}</span>
                    {nonBusinessSourceCategory === 'retirement_accounts' && monthlyWithdrawal > 0 && (
                      <span className="text-xs text-purple-600">After withdrawals</span>
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="15"
                      step="0.1"
                      value={postExitRetirementAccountsReturnRate}
                      onChange={(e) => setPostExitRetirementAccountsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-purple-700 text-center">{postExitRetirementAccountsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-orange-600 block">{formatCurrency(retirementAccountsFVEndOfLife)}</span>
                    {postExitSourceCategory === 'retirement_accounts' && postExitMonthlyWithdrawal > 0 ? (
                      <span className="text-xs text-purple-600">-{formatCurrency(postExitMonthlyWithdrawal)}/mo</span>
                    ) : (
                      <span className="text-xs text-gray-500">{yearsPostExit} yrs</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Real Estate Equity */}
              <div className={`grid grid-cols-12 gap-2 items-center ${(nonBusinessSourceCategory === 'real_estate' || nonBusinessSourceCategory === 'real_estate_equity') ? 'bg-purple-50 p-3 rounded-lg border-2 border-purple-300' : 'py-2'}`}>
                <div className="col-span-12 lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-900 flex items-center gap-1">
                    Real Estate Equity
                    <HelpTooltip content="Net equity in your primary residence, vacation homes, and investment properties (not including your business real estate). Typical return: 3-5% annually." />
                  </label>
                  {(nonBusinessSourceCategory === 'real_estate' || nonBusinessSourceCategory === 'real_estate_equity') && monthlyWithdrawal > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs font-bold text-purple-700">-{formatCurrency(monthlyWithdrawal)}/mo</span>
                    </div>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="relative">
                    <span className="absolute left-2 top-2 text-gray-500 text-xs">$</span>
                    <input
                      type="text"
                      value={formatNetWorthValue(realEstateEquity)}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || !isNaN(value)) {
                          setRealEstateEquity(value);
                        }
                      }}
                      placeholder="750,000"
                      className="w-full pl-6 pr-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={realEstateReturnRate}
                      onChange={(e) => setRealEstateReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-blue-700 text-center">{realEstateReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-emerald-600 block">{formatCurrency(realEstateFV)}</span>
                    {(nonBusinessSourceCategory === 'real_estate' || nonBusinessSourceCategory === 'real_estate_equity') && monthlyWithdrawal > 0 && (
                      <span className="text-xs text-purple-600">After withdrawals</span>
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={postExitRealEstateReturnRate}
                      onChange={(e) => setPostExitRealEstateReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-purple-700 text-center">{postExitRealEstateReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-orange-600 block">{formatCurrency(realEstateFVEndOfLife)}</span>
                    {(lumpSumAmount > 0 && lumpSumAllocRealEstate > 0) || (monthlyEarnoutPayment > 0 && earnoutAllocRealEstate > 0) ? (
                      <span className="text-xs text-green-600">
                        {lumpSumAmount > 0 && lumpSumAllocRealEstate > 0 && `+${lumpSumAllocRealEstate}% lump`}
                        {lumpSumAmount > 0 && lumpSumAllocRealEstate > 0 && monthlyEarnoutPayment > 0 && earnoutAllocRealEstate > 0 && ', '}
                        {monthlyEarnoutPayment > 0 && earnoutAllocRealEstate > 0 && `+${earnoutAllocRealEstate}% earnout`}
                      </span>
                    ) : (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') && postExitMonthlyWithdrawal > 0 ? (
                      <span className="text-xs text-purple-600">-{formatCurrency(postExitMonthlyWithdrawal)}/mo</span>
                    ) : (
                      <span className="text-xs text-gray-500">{yearsPostExit} yrs</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Other Investments */}
              <div className={`grid grid-cols-12 gap-2 items-center ${nonBusinessSourceCategory === 'other_investments' ? 'bg-purple-50 p-3 rounded-lg border-2 border-purple-300' : 'py-2'}`}>
                <div className="col-span-12 lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-900 flex items-center gap-1">
                    Other Investments
                    <HelpTooltip content="Stocks, bonds, mutual funds, ETFs, and other investment accounts not included in retirement accounts. Typical return: 5-8% annually depending on allocation." />
                  </label>
                  {nonBusinessSourceCategory === 'other_investments' && monthlyWithdrawal > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs font-bold text-purple-700">-{formatCurrency(monthlyWithdrawal)}/mo</span>
                    </div>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="relative">
                    <span className="absolute left-2 top-2 text-gray-500 text-xs">$</span>
                    <input
                      type="text"
                      value={formatNetWorthValue(otherInvestments)}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || !isNaN(value)) {
                          setOtherInvestments(value);
                        }
                      }}
                      placeholder="300,000"
                      className="w-full pl-6 pr-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="15"
                      step="0.1"
                      value={otherInvestmentsReturnRate}
                      onChange={(e) => setOtherInvestmentsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-blue-700 text-center">{otherInvestmentsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-emerald-600 block">{formatCurrency(otherInvestmentsBaseFV)}</span>
                    {nonBusinessSourceCategory === 'other_investments' && monthlyWithdrawal > 0 && (
                      <span className="text-xs text-purple-600">After withdrawals</span>
                    )}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="15"
                      step="0.1"
                      value={postExitOtherInvestmentsReturnRate}
                      onChange={(e) => setPostExitOtherInvestmentsReturnRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-purple-700 text-center">{postExitOtherInvestmentsReturnRate}%</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-orange-600 block">{formatCurrency(otherInvestmentsFVEndOfLife)}</span>
                    {(lumpSumAmount > 0 && lumpSumAllocOtherInv > 0) || (monthlyEarnoutPayment > 0 && earnoutAllocOtherInv > 0) ? (
                      <span className="text-xs text-green-600">
                        {lumpSumAmount > 0 && lumpSumAllocOtherInv > 0 && `+${lumpSumAllocOtherInv}% lump`}
                        {lumpSumAmount > 0 && lumpSumAllocOtherInv > 0 && monthlyEarnoutPayment > 0 && earnoutAllocOtherInv > 0 && ', '}
                        {monthlyEarnoutPayment > 0 && earnoutAllocOtherInv > 0 && `+${earnoutAllocOtherInv}% earnout`}
                      </span>
                    ) : postExitSourceCategory === 'other_investments' && postExitMonthlyWithdrawal > 0 ? (
                      <span className="text-xs text-purple-600">-{formatCurrency(postExitMonthlyWithdrawal)}/mo</span>
                    ) : (
                      <span className="text-xs text-gray-500">{yearsPostExit} yrs</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Total Liabilities */}
              <div className="grid grid-cols-12 gap-2 items-center pt-3 border-t-2 border-gray-300 py-2">
                <div className="col-span-12 lg:col-span-2">
                  <label className="block text-xs font-semibold text-gray-900">Total Liabilities</label>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="relative">
                    <span className="absolute left-2 top-2 text-gray-500 text-xs">$</span>
                    <input
                      type="text"
                      value={formatNetWorthValue(totalLiabilities)}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || !isNaN(value)) {
                          setTotalLiabilities(value);
                        }
                      }}
                      placeholder="200,000"
                      className="w-full pl-6 pr-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="20"
                      step="0.5"
                      value={liabilityReductionRate}
                      onChange={(e) => setLiabilityReductionRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                      <span className="text-xs font-bold text-blue-700 text-center">{liabilityReductionRate}% (Annual reduction rate)</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-red-600 block">{formatCurrency(futureLiabilities)}</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="flex flex-col gap-1">
                    <input
                      type="range"
                      min="0"
                      max="20"
                      step="0.5"
                      value={postExitLiabilityReductionRate}
                      onChange={(e) => setPostExitLiabilityReductionRate(e.target.value)}
                      className="w-full h-1.5 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-lg appearance-none cursor-pointer slider-thumb"
                    />
                    <span className="text-xs font-bold text-purple-700 text-center">{postExitLiabilityReductionRate}% (Annual reduction rate)</span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-2">
                  <div className="text-center">
                    <span className="text-sm font-bold text-red-600 block">{formatCurrency(liabilitiesFVEndOfLife)}</span>
                    <span className="text-xs text-gray-500">{yearsPostExit} yrs</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculated Net Worth Display */}
            {(liquidAssets || retirementAccounts || realEstateEquity || otherInvestments || totalLiabilities) && (
              <div className="mt-4">
                <div className="p-4 bg-gradient-to-r from-blue-50 via-emerald-50 to-orange-50 border-2 border-blue-200 rounded-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Current Net Worth */}
                    <div className="border-r border-gray-300 pr-4">
                      <span className="text-xs font-semibold text-gray-600 block mb-1">Current Non-Business Net Worth:</span>
                      <span className="text-xl font-bold text-blue-600 block">
                        {formatCurrency(calculatedNetWorth)}
                      </span>
                    </div>

                    {/* Future Net Worth at Exit */}
                    <div className="border-r border-gray-300 pr-4">
                      <span className="text-xs font-semibold text-gray-600 block mb-1">
                        Non-Business Net Worth at Exit (Age {retAge}):
                      </span>
                      <span className="text-xl font-bold text-emerald-600 block">
                        {formatCurrency(nonBusinessNetWorthAtExit)}
                      </span>
                      {yearsUntilExit > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          In {yearsUntilExit} years
                        </p>
                      )}
                      {lumpSumAmount > 0 && (
                        <p className="text-xs text-blue-600 mt-1 font-semibold">
                          + {formatCurrency(lumpSumAmount)} from business sale
                          {lumpSumAllocLiquid > 0 && ` (${lumpSumAllocLiquid}% Liquid Assets${(lumpSumAllocRealEstate > 0 || lumpSumAllocOtherInv > 0) ? ',' : ''})`}
                          {lumpSumAllocRealEstate > 0 && ` (${lumpSumAllocRealEstate}% Real Estate${lumpSumAllocOtherInv > 0 ? ',' : ''})`}
                          {lumpSumAllocOtherInv > 0 && ` (${lumpSumAllocOtherInv}% Other Investments)`}
                        </p>
                      )}
                    </div>

                    {/* Future Net Worth at End of Life */}
                    <div>
                      <span className="text-xs font-semibold text-gray-600 block mb-1">
                        Total Net Worth at Age {lifeExpectancy}:
                      </span>
                      <span className="text-xl font-bold text-orange-600 block">
                        {formatCurrency(netWorthAtEndOfLife)}
                      </span>
                      {yearsPostExit > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {yearsPostExit} years post-exit
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </CollapsibleSection>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-700 hover:text-gray-900 font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2 font-semibold"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={20} />
                <span>Save Wealth Gap</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

WealthGapCalculatorModalEnhanced.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  wealthGap: PropTypes.object,
  businessProfile: PropTypes.object,
  profitGap: PropTypes.object,
  valueGap: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired
};

export default WealthGapCalculatorModalEnhanced;
